import Button from '@/se/components/Button[DEPRECATED]';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { orderBy } from 'lodash';
import filter from 'lodash/filter';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import waiting from '../../../../assets/images/waiting.svg';
import { useSetRoomMutation } from '@/hooks/useSetRoomMutation';
import { useToaster } from '../../../core/Toaster';
import { useModalState } from '@/util/useModalState';
import { CaretakerChatDialog } from '../../../chat/ChatDialog';
import ClientUpdater from '../../../ClientUpdater';
import { OR, PACU, POST_OP, PRE_OP, ROOM_TYPES, WAITING_ROOM } from '../../../entities/room/enums';
import { getScreenType } from '../../../entities/screens/enums';
import { AutomaticPatientTracking } from '../../../ModuleOptional';
import PatientSlot from '../../../patient/PatientSlot/PatientSlot';
import PatientSelection from '../nursingApp/PatientSelection';
import Troubleshooting from '../tablet/Troubleshooting';
import { getFrameColor, getLogEntries, isPreOpAsDPU, isReadyForFamilyPreOp, getEventFlags } from '../tablet/utils';

const SlotContainerWrapper = styled.div`
  flex: 1;
  ${props =>
    props.columns > 1 &&
    `
    flex: 1;
    columns: 2;
    column-fill: auto;
  `};
`;

const SlotContainer = styled.div`
  margin-bottom: 0.5rem;
  cursor: pointer;
  break-inside: avoid;
  display: inline-block;
  width: 100%;
  position: relative;
`;

const EmptyState = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    flex={1}
    style={{ gap: '1em' }}
  >
    <div>
      <img src={waiting} alt="An illustration of a sand dial" />
    </div>
    <Typography variant="h1">Waiting for patients</Typography>
    <Typography color="textSecondary">
      Once a patient enters the room, this screen will automatically show the controls.
    </Typography>
  </Box>
);

const MovePatient = ({ room }) => {
  const [modal, setModal] = useState(false);
  const [moveTo] = useSetRoomMutation();
  const toaster = useToaster();

  const filterPatients = patients => {
    const targetRoom =
      room?.type === PRE_OP
        ? [WAITING_ROOM]
        : room?.type === PACU
          ? [WAITING_ROOM, PRE_OP, OR]
          : [WAITING_ROOM, PRE_OP, OR, PACU, POST_OP];
    return orderBy(
      filter(
        patients,
        patient => targetRoom.includes(get(patient, 'room.type')) && get(patient, 'room.name') !== get(room, 'name')
      ),
      'room.type'
    );
  };

  const movePatient = id => async () => {
    try {
      const result = await moveTo({ variables: { id, roomId: get(room, 'id') } });
      if (result?.data?.setRoomForPatient === false) {
        toaster.warning('Room change is already in progress for this patient. Please try again.');
      }
      setModal(false);
    } catch (e) {
      console.log(e);
    }
  };

  if (modal) {
    return (
      <PatientSelection
        handleCancel={() => setModal(false)}
        handlePatientClick={movePatient}
        filterFn={filterPatients}
        TitleAndActions={() => null}
        withRoom={true}
      />
    );
  }

  return <Button onClick={() => setModal(true)}>{`Move Patient To ${get(room, 'name', 'Here')}`}</Button>;
};

const MovePatients = AutomaticPatientTracking(null, MovePatient);

const PatientList = props => {
  const classes = useStyles();
  const { open, handleClose, handleOpen } = useModalState();

  const { hasNoteModule, hospitalOverviewMode, roomType, roomName, room, hospitalId } = props;

  const isPreOp = roomType === PRE_OP;
  const isPacu = roomType === PACU;
  const isPostOp = roomType === POST_OP;

  const patients = props.patients.map(patient => {
    const events = patient.events || {};
    const logEntries = getLogEntries(patient);
    const eventFlags = getEventFlags(events, roomType);

    const isReady = eventFlags.isReady;

    const isFamilyReadyPreOp = isReadyForFamilyPreOp(logEntries)(roomType);

    const isHoldProcedure = props.showHoldProcedure ? eventFlags.isHoldProcedure : patient.isHoldProcedure;

    const isBlockNerve =
      props.showBlockNerve || (props.showBlockNervePacu && get(patient, 'blockNerveFollow', true))
        ? eventFlags.isBlockNerveActive
        : patient.isBlockNerve;

    const isReadyForSurgeon = props.showReadyForSurgeon ? eventFlags.isReadyForSurgeon : patient.isReadyForSurgeon;

    const isReadyForAnesthesia = props.showReadyForAnesthesia
      ? eventFlags.isReadyForAnesthesia
      : patient.isReadyForAnesthesia;
    const isSpinalTimeout = props.showSpinalTimeout ? eventFlags.isSpinalTimeout : patient.isSpinalTimeout;

    return {
      ...patient,
      isReady,
      isFamilyReadyPreOp,
      isHoldProcedure,
      isBlockNerve,
      isReadyForSurgeon,
      isReadyForAnesthesia,
      isSpinalTimeout,
    };
  });

  const isInteractiveLayout = props.layoutVariant === 'interactive';
  const columns = isInteractiveLayout ? 1 : patients.length < 11 ? 1 : 2;

  function handleListItemClick(e, patient) {
    if (!hospitalOverviewMode) {
      props.selectPatient(patient.id);
      return;
    }

    if (patient.caretakerConsent) {
      handleOpen();
    }
  }

  return (
    <Box flex={1} display="flex" flexDirection="column">
      <Box className={classes.header} display="flex" alignItems="center" justifyContent="space-between">
        {roomName ? <Typography variant="h1">{roomName}</Typography> : <Typography variant="h1">Patients</Typography>}
        {room && !(Array.isArray(room) && room.length === 0) && <MovePatients room={room} />}
      </Box>
      <Box flex={1} display="flex" flexDirection="column" px={2} py={2}>
        {patients.length === 0 ? (
          <EmptyState />
        ) : (
          <SlotContainerWrapper columns={columns}>
            {patients.length > 0 ? (
              patients.map(patient => (
                <SlotContainer key={patient.id} onClick={e => handleListItemClick(e, patient)}>
                  <PatientSlot
                    patient={patient}
                    {...patient}
                    showPriority={props.hasPreOpPriorityModule}
                    caretakerMessage={get(patient, 'caretakerMessages', null)}
                    status="occupied"
                    frameColor={getFrameColor(roomType, patient?.log)}
                    ready={patient.isReady}
                    isHoldProcedure={patient.isHoldProcedure}
                    isBlockNerve={patient.isBlockNerve}
                    readyForAnesthesia={patient.isReadyForAnesthesia}
                    isSpinalTimeout={patient.isSpinalTimeout}
                    readyForSurgeon={patient.isReadyForSurgeon}
                    readyForOr={patient.isReady && roomType === ROOM_TYPES.PRE_OP}
                    familyReady={isPacu ? patient.familyReadyPACU : isPostOp ? patient.familyReadyPOSTOP : false}
                    readyForPickup={
                      isPacu ? patient.readyForPickupPACU : isPostOp ? patient.readyForPickupPOSTOP : false
                    }
                    preventAnimation
                    size={patients.length < 8 ? 'large' : null}
                    screenType={getScreenType(roomType)}
                    patientData={patient}
                    showPreOpNote={isPreOp && hasNoteModule}
                    showPacuNote={isPacu && hasNoteModule}
                    preOpNote={patient?.preOpNote}
                    pacuNote={patient?.pacuNote}
                    hospitalOverviewMode={hospitalOverviewMode}
                    showRoom={hospitalOverviewMode}
                    showDuration={!hospitalOverviewMode}
                    roomName={patient.room?.name}
                    isDPU={isPreOpAsDPU(roomType, getLogEntries(patient))}
                    blockNerveFollow={patient?.blockNerveFollow}
                    called={patient?.called}
                    transferredToChair={patient?.transferredToChair}
                    transferredToPT={patient?.transferredToPT}
                    ptComplete={patient?.ptComplete}
                    isPreOp={isPreOp}
                    isPacu={isPacu}
                    showLatePreOpIndicator={props?.showLatePreOpIndicator}
                    showPatientAgeAndSex={props.showPatientAgeAndSex}
                    showPatientProcedureTimes={props.showPatientProcedureTimes}
                    showPatientScheduledOr={props.showPatientScheduledOr}
                    firstCase={props.getFirstCaseStatus?.(patient) ?? null}
                    room={room}
                    hospitalId={hospitalId}
                  />

                  {patient.caretakerConsent ? (
                    <CaretakerChatDialog patient={patient} open={open} handleClose={handleClose} withButton={false} />
                  ) : null}
                </SlotContainer>
              ))
            ) : (
              <ClientUpdater />
            )}
          </SlotContainerWrapper>
        )}
      </Box>

      <Box className={classes.footer}>
        <Troubleshooting roomName={roomName} />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    position: 'sticky',
    bottom: 0,
  },
}));

PatientList.propTypes = {
  roomType: PropTypes.string,
  roomName: PropTypes.string,
  selectPatient: PropTypes.func.isRequired,
  patients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  hospitalId: PropTypes.number,
  getFirstCaseStatus: PropTypes.func,
};

export default PatientList;
